import React, { useState, ChangeEvent } from "react";
import Link from "next/link";
import {
  Input,
  FormGroup,
  Container,
  Table,
  Row,
  Col,
  Badge,
} from "reactstrap";
import useApi from "~/hooks/use-api";
import { CheckCircleFillIcon, XCircleIcon } from "@primer/octicons-react";
import Button from "~/components/Button";

type Organization = {
  uid: string;
  name: string;
  timezone: string;
  currency: string;
  disabled: string;
  accountManager: string | null;
};

type FeatureFlagDefinition = {
  name: string;
  description?: string;
  strategies: Array<{
    name: string;
    parameters: Record<string, any>;
  }>;
};

const badgeStyle: React.CSSProperties = {
  marginRight: 5,
  marginBottom: 5,
  cursor: "default",
};

export function FeatureFlagStrategy({
  strategy,
}: {
  strategy: { name: string; parameters: Record<string, any> };
}) {
  const color = strategy.name === "default" ? "success" : "primary";
  const title = JSON.stringify(strategy.parameters, null, 2);

  return (
    <Badge style={badgeStyle} color={color} title={title}>
      {strategy.name}
    </Badge>
  );
}

export default function FeatureFlags() {
  const { data: featureFlags } =
    useApi<FeatureFlagDefinition[]>("/api/feature-flags");

  return (
    <Container>
      <Row form className="mt-4">
        <Col sm="9">
          <h2>Feature Flags</h2>
        </Col>
        <Col sm="3" className="text-right">
          <Link href="https://unleash.loke.global" passHref>
            <Button outline color="secondary" tag="a">
              Unleash
            </Button>
          </Link>
        </Col>
      </Row>
      {featureFlags && (
        <>
          <Table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Strategies</th>
              </tr>
            </thead>
            <tbody>
              {featureFlags.map((ff) => {
                return (
                  <tr key={ff.name}>
                    <td>
                      <Link
                        href="/tools/feature-flags/[name]"
                        as={`/tools/feature-flags/${ff.name}`}
                      >
                        <a>{ff.name}</a>
                      </Link>
                    </td>
                    <td>
                      {ff.strategies.map((strategy) => (
                        <FeatureFlagStrategy
                          key={strategy.name}
                          strategy={strategy}
                        />
                      ))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </>
      )}
    </Container>
  );
}
